import { Box, Container, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '../../../../../components/ui/buttons/Button';
import { useOnce, useOnceWhen } from '../../../../../hooks';
// TODO: Uncomment when phone upload flow is wired up
// import QRPlaceholder from './components/QRPlaceholder';
import {
  trackPhotoCaptureStarted,
  trackPhotosCompleted,
} from '../../../../../services/analytics/marketplaceEvents';
import { PHOTOS_REQUIRED } from '../constants';
import CategorySection from './components/CategorySection';
import PhotoLightbox from './components/PhotoLightbox';
import PhotoUploadHeader from './components/PhotoUploadHeader';
import { CATEGORIES, PHOTO_INPUT_ACCEPT, PHOTO_SLOTS } from './constants';
import { usePhotoUpload } from './hooks/usePhotoUpload';

type PhotosPageProps = {
  dealId: string;
};

const PhotosPage = ({ dealId }: PhotosPageProps) => {
  const {
    loadError,
    isRetryingLoad,
    retryLoad,
    photos,
    processingSlotIds,
    lastFilledSlotId,
    uploadedCount,
    listingStatus,
    fileInputRef,
    handleSlotClick,
    handleFileSelected,
    handleFileDrop,
    handleDelete,
    handleReplace,
    clearLastFilled,
  } = usePhotoUpload(dealId);
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [viewingSlotId, setViewingSlotId] = useState<string | null>(null);
  const [pulsingSlotId, setPulsingSlotId] = useState<string | null>(null);
  const slotRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const isComplete = uploadedCount >= PHOTOS_REQUIRED;
  const isPostAcceptance = listingStatus === 'accepted';
  useOnce(() => trackPhotoCaptureStarted(dealId));
  useOnceWhen(isComplete, () => trackPhotosCompleted(dealId));

  const openLightbox = useCallback(
    (slotId: string) => {
      setViewingSlotId(slotId);
      onOpen();
    },
    [onOpen],
  );

  useEffect(() => {
    if (!lastFilledSlotId) return undefined;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const allSlotIds = PHOTO_SLOTS.map((s) => s.id);
    const filledIndex = allSlotIds.indexOf(lastFilledSlotId);

    const nextEmptyId = [
      ...allSlotIds.slice(filledIndex + 1),
      ...allSlotIds.slice(0, filledIndex),
    ].find((id) => !photos.has(id));

    if (nextEmptyId) {
      const el = slotRefs.current.get(nextEmptyId);
      if (el) {
        timers.push(
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300),
        );
      }
      setPulsingSlotId(nextEmptyId);
      timers.push(setTimeout(() => setPulsingSlotId(null), 3000));
    }

    clearLastFilled();
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [lastFilledSlotId, photos, clearLastFilled]);

  const viewingPhoto = viewingSlotId ? photos.get(viewingSlotId) : undefined;
  const viewingSlot = viewingSlotId ? PHOTO_SLOTS.find((s) => s.id === viewingSlotId) : undefined;

  useEffect(() => {
    if (!loadError) return;

    toast({
      title: 'Could not refresh marketplace photos',
      description: 'Please try again.',
      status: 'error',
      duration: 4000,
      isClosable: true,
      position: 'top',
    });
  }, [loadError, toast]);

  return (
    <Container maxW="container.xl" my="20px" px={{ base: '16px', md: '24px' }}>
      <PhotoUploadHeader uploadedCount={uploadedCount} isPostAcceptance={isPostAcceptance} />

      {loadError && (
        <Flex
          mt={4}
          mb={4}
          bg="white"
          border="1px solid"
          borderColor="grayMediumBackground"
          borderRadius="12px"
          p={4}
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={3}
        >
          <Box>
            <Text fontSize="sm" fontWeight="700" color="grayDark">
              Could not refresh photos right now.
            </Text>
            <Text fontSize="sm" color="grayLight">
              Your current photo state is still shown.
            </Text>
          </Box>
          <Button
            onClick={retryLoad}
            isLoading={isRetryingLoad}
            loadingText="Retrying..."
            bg="oceanBoatBlue"
            color="white"
            _hover={{ bg: 'royalBlueDark' }}
            trackClick="marketplace_photos_retry_load"
          >
            Try again
          </Button>
        </Flex>
      )}

      <Box maxW="900px" mx="auto" mt={6}>
        {/* TODO: Unhide when phone upload flow is wired up */}
        {/* <QRPlaceholder /> */}

        {CATEGORIES.map((category) => (
          <CategorySection
            key={category.key}
            category={category}
            photos={photos}
            processingSlotIds={processingSlotIds}
            pulsingSlotId={pulsingSlotId}
            onSlotClick={handleSlotClick}
            onViewPhoto={openLightbox}
            onDelete={handleDelete}
            onFileDrop={handleFileDrop}
            slotRefs={slotRefs}
            isPostAcceptance={isPostAcceptance}
          />
        ))}
      </Box>

      <PhotoLightbox
        isOpen={isOpen}
        onClose={onClose}
        photo={viewingPhoto}
        slotLabel={viewingSlot?.label ?? ''}
        onReplace={() => viewingSlotId && handleReplace(viewingSlotId)}
        onDelete={() => viewingSlotId && handleDelete(viewingSlotId)}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_INPUT_ACCEPT}
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
    </Container>
  );
};

export default PhotosPage;
